'use client';

import { memo, useEffect, useMemo, useRef } from 'react';

import { homeV2 } from '@/features/v2/home/components/privateHome/localization/core';
import { NavigationButton } from '@/features/v2/shared/navegation/NavegationButton';
import { cn } from '@/lib/utils';
import { useAuthStore, UserAccount } from '@/stores/userStore';
import { userUIStore } from '@/stores/userUIStore';
import { HButton } from '@/stories/atoms/buttons/mainButton/Button';

function HeaderReference() {
  const kycLevel = useAuthStore((state) => { return state.account?.kycLevel; });
  const kycStatus = useAuthStore((state) => { return state.account?.kycStatus; });
  const lastDeposit = useAuthStore((state) => { return state.account?.lastDeposit; });
  const accountId = useAuthStore((state) => { return state.account?.id; });
  const setAccount = useAuthStore((state) => { return state.setAccount; });
  const isSidebarOpen = userUIStore((state) => { return state.isSidebarOpen; });
  const hasFetchedRef = useRef(false);
  const fetchingRef = useRef(false);
  const lastKycLevelRef = useRef<number | undefined>(undefined);
  const lastKycStatusRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const kycChanged = lastKycLevelRef.current !== kycLevel || lastKycStatusRef.current !== kycStatus;

    if (kycChanged) {
      lastKycLevelRef.current = kycLevel;
      lastKycStatusRef.current = kycStatus;
      hasFetchedRef.current = false;
    }

    if (!accountId || hasFetchedRef.current) {
      return;
    }

    if (kycLevel !== undefined && kycStatus !== undefined && !kycChanged) {
      hasFetchedRef.current = true;
      return;
    }

    const fetchKYCStatus = async () => {
      if (fetchingRef.current) return;

      fetchingRef.current = true;
      try {
        const response = await fetch('/api/kyc-verification/kyc-status');
        if (!response.ok) throw new Error('Error al obtener el estado KYC');

        const data = await response.json();
        const currentAccount = useAuthStore.getState().account;

        if (currentAccount && (currentAccount.kycLevel !== data.kyc_level || currentAccount.kycStatus !== data.kyc_status)) {
          const updatedAccount: UserAccount = {
            ...currentAccount,
            kycLevel: data.kyc_level ?? currentAccount.kycLevel ?? 0,
            kycStatus: data.kyc_status ?? currentAccount.kycStatus ?? 0,
          };
          setAccount(updatedAccount);
        }
        hasFetchedRef.current = true;
      } catch (err) {
        console.warn('[HeaderReference] Error al obtener el estado KYC:', err);
      } finally {
        fetchingRef.current = false;
      }
    };

    fetchKYCStatus();
  }, [accountId, kycLevel, kycStatus, setAccount]);

  const content = useMemo(() => {
    switch (true) {
      case kycLevel === 0:
        return homeV2.kyc1;
      case kycStatus === 2 && !lastDeposit:
        return homeV2.noDeposit;
      default:
        return homeV2.complete;
    }
  }, [kycLevel, kycStatus, lastDeposit]);

  return (
    <div className={cn(
      'flex flex-col items-center justify-center w-full md:ms-28 md:max-w-xl md:mt-12 z-50 px-4',
      isSidebarOpen ? 'md:ms-52' : 'md:ms-0',
    )}
    >
      <h2 className="text-white text-2xl md:text-5xl font-bold font-title">
        {content.title}
      </h2>
      <p className="text-white text-base md:ml-6 md:text-xl text-center font-text leading-tight mt-2 pb-2">
        {content.description}
        <span className="text-primary-500 px-2">{content.span}</span>
        {content.and}
      </p>
      <NavigationButton href={content.buttonLink} className="flex justify-center w-full mt-4 max-w-md mx-auto">
        <HButton
          label={content.button}
          size="medium"
          mode="secondary"
          disabled={false}
          aria-label="Unirse ahora a Powerbet"
          fat
          withShadow
        />
      </NavigationButton>
    </div>
  );
}

export default memo(HeaderReference);
